import { useTranslation } from 'react-i18next'

/**
 * VH-236: what the chat read before it answered.
 *
 * Record and analysis lookups show as one line each ("recipe · 1 result"), so the user can see the
 * answer is based on their own data rather than on a guess. A fetched page is different: it is
 * someone else's content, so it is shown as a preview with its source URL, and nothing is created
 * from it until the user acts on it.
 */
export function ChatLookups({ lookups }) {
  const { t } = useTranslation()
  if (!lookups?.length) return null

  return (
    <div className="mt-1 rounded-md border border-gray-200 bg-surface-container p-2 text-xs" data-testid="chat-lookups">
      <p className="font-medium text-gray-700">{t('healthCycle.chat.lookups.title')}</p>
      <ul className="mt-1 space-y-1">
        {lookups.map((lookup, index) => (
          <li key={`${lookup.kind}-${index}`} className="text-gray-600">
            <span className="font-medium">{t(`healthCycle.chat.lookups.kind.${lookup.kind}`, lookup.kind)}</span>
            {lookup.query ? <span className="ml-1 font-mono">{lookup.query}</span> : null}
            {lookup.error ? (
              <span className="ml-1 text-amber-700">
                {t(`healthCycle.chat.lookups.error.${lookup.error}`, lookup.error)}
              </span>
            ) : lookup.count !== null && lookup.count !== undefined ? (
              <span className="ml-1">{t('healthCycle.chat.lookups.results', { count: lookup.count })}</span>
            ) : null}
            {lookup.page && <PagePreview page={lookup.page} />}
          </li>
        ))}
      </ul>
    </div>
  )
}

function PagePreview({ page }) {
  const { t } = useTranslation()
  const items = page.recipe?.ingredients ?? page.items ?? []
  return (
    <div className="mt-1 rounded border border-gray-200 p-2" data-testid="chat-fetched-page">
      <p className="font-medium text-gray-800">{page.title || page.final_url}</p>
      <p className="text-gray-500 break-all">{t('healthCycle.chat.lookups.source', { url: page.final_url })}</p>
      <p className="text-gray-500">{t('healthCycle.chat.lookups.untrusted')}</p>
      {items.length > 0 && (
        <ul className="mt-1 list-disc pl-4 text-gray-700">
          {items.slice(0, 15).map((item, index) => (
            <li key={`${item.name}-${index}`}>{item.text || item.name}</li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-gray-500">{t('healthCycle.chat.lookups.notStored')}</p>
    </div>
  )
}

export default ChatLookups
